import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { getListProduct } from "../../../services";
import { ProductListItem } from "../../../types/product.type";
import { useDebounce } from "../../../utils/hooks/useDebounce";

export interface ProductOption {
  uuid: string;
  code: string;
  name: string;
}

const PRODUCT_SEARCH_LIMIT = 20;

/** What makes two options the same product: its uuid (default), or its code (BDC SKU link). */
export type ProductIdentity = "uuid" | "code";

/**
 * Pick the GB product a 7-11 SKU maps to. Searches GET /products/list (name / code / barcode)
 * as the user types; the barcode of the 7-11 SKU is the natural first query.
 */
export default function ProductPicker({
  value,
  initialQuery,
  disabled,
  identity = "uuid",
  onChange,
}: Readonly<{
  value: ProductOption | null;
  initialQuery: string;
  disabled?: boolean;
  identity?: ProductIdentity;
  onChange: (option: ProductOption | null) => void;
}>) {
  const keyOf = (o: ProductOption) => (identity === "code" ? o.code : o.uuid);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const query = useDebounce(input.trim(), 300);
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    getListProduct(1, PRODUCT_SEARCH_LIMIT, { search: query || initialQuery })
      .then(({ data }) => {
        if (!active) return;
        const payload = data?.data;
        const rows: ProductListItem[] = Array.isArray(payload) ? payload : payload?.rows ?? [];
        const mapped = rows.map((p) => ({ uuid: p.uuid, code: p.code, name: p.name }));
        // by code, one product code is one option even when several rows share it
        setOptions(
          identity === "code"
            ? mapped.filter((o, i) => mapped.findIndex((x) => x.code === o.code) === i)
            : mapped,
        );
      })
      .catch(() => {
        if (active) setOptions([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, query, initialQuery, identity]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      options={value && !options.some((o) => keyOf(o) === keyOf(value)) ? [value, ...options] : options}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      isOptionEqualToValue={(a, b) => keyOf(a) === keyOf(b)}
      getOptionLabel={(o) => `${o.code} · ${o.name}`}
      onInputChange={(_e, text, reason) => {
        if (reason === "input") setInput(text);
      }}
      onChange={(_e, option) => onChange(option)}
      noOptionsText="ไม่พบสินค้า"
      loadingText="กำลังค้นหา…"
      sx={{ minWidth: 280 }}
      renderInput={(params) => <TextField {...params} placeholder="ค้นหาสินค้า GB" />}
    />
  );
}
